/**
 * Call-transfer callback sessions — booked when a transfer target is busy.
 *
 * Not sourced from AHP: no check-in/out, no journey tracking, no reminders.
 * Optional one-time location snap at book time for safety.
 */

import type { CallbackSessionMeta, GeoStamp, Session } from './types'

/** Default callback window length when no explicit end is supplied. */
export const CALLBACK_SESSION_DURATION_MS = 30 * 60 * 1000

export const CALLBACK_SERVICE_TYPE = 'Callback (call transfer)'

export function isCallbackSession(
  session: Pick<Session, 'source' | 'callback'>,
): boolean {
  return session.source === 'callback' || session.callback != null
}

/** Callback sessions never receive AHP-style reminders. */
export function isRemindableSession(
  session: Pick<Session, 'coreStatus' | 'lifecycleState' | 'source' | 'callback'>,
): boolean {
  if (isCallbackSession(session)) return false
  return (
    session.coreStatus !== 'cancelled' &&
    session.lifecycleState !== 'checked-out' &&
    session.lifecycleState !== 'synced' &&
    session.lifecycleState !== 'cancelled' &&
    session.lifecycleState !== 'not-started'
  )
}

function localTimezoneOffsetLabel(date: Date): string {
  const offsetMin = -date.getTimezoneOffset()
  const sign = offsetMin >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMin)
  const hours = String(Math.floor(abs / 60)).padStart(2, '0')
  const mins = String(abs % 60).padStart(2, '0')
  return `${sign}${hours}:${mins}`
}

export type BuildCallbackSessionInput = {
  uuid: string
  providerEmail: string
  providerName: string
  memberEmail: string
  memberName: string
  callId: string
  bookedByUserId: string
  bookedByDisplayName?: string
  /** Defaults to now. */
  scheduledStartMs?: number
  /** Defaults to start + {@link CALLBACK_SESSION_DURATION_MS}. */
  scheduledEndMs?: number
  notes?: string
  locationSnap?: GeoStamp | null
}

/** Build a telehealth callback session row ready for DynamoDB upsert. */
export function buildCallbackSession(input: BuildCallbackSessionInput): Session {
  const start = input.scheduledStartMs ?? Date.now()
  const end = input.scheduledEndMs ?? start + CALLBACK_SESSION_DURATION_MS
  const at = new Date(start)
  const callback: CallbackSessionMeta = {
    callId: input.callId,
    bookedByUserId: input.bookedByUserId,
    bookedByDisplayName: input.bookedByDisplayName,
    locationSnap: input.locationSnap ?? null,
  }

  return {
    uuid: input.uuid,
    seriesUuid: null,
    providerEmail: input.providerEmail.trim().toLowerCase(),
    providerName: input.providerName.trim(),
    memberEmail: input.memberEmail.trim().toLowerCase(),
    memberName: input.memberName.trim(),
    locationText: 'Telehealth callback',
    geo: input.locationSnap ? { lat: input.locationSnap.lat, lng: input.locationSnap.lng } : null,
    serviceType: CALLBACK_SERVICE_TYPE,
    faceToFace: false,
    nonFaceToFaceSessionType: 'callback',
    start,
    end,
    timezone: localTimezoneOffsetLabel(at),
    coreStatus: 'confirmed',
    notes: input.notes?.trim() ?? '',
    checkins: [],
    lifecycleState: 'scheduled',
    actualStart: null,
    actualEnd: null,
    checkIn: null,
    checkOut: null,
    kmTracked: null,
    driveSegments: [],
    noteStatus: 'none',
    source: 'callback',
    callback,
  }
}
